import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Store } from "lucide-react";
import { api } from "@/lib/api";
import { useTitle } from "@/lib/format";
import { PageLoader, EmptyState } from "@/components/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DashboardHeader, StatsGrid, OverviewTab, ProviderBookings } from "@/components/provider/DashboardParts";
import ProviderServices from "@/components/provider/ProviderServices";
import ProviderAvailability from "@/components/provider/ProviderAvailability";
import { ProviderReviews, ProviderProfileForm } from "@/components/provider/ProviderExtras";

export default function ProviderDashboard() {
  useTitle("Provider Dashboard");
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data: me } = await api.get("/provider/me");
      setData(me);
      if (me.provider) {
        const [s, b] = await Promise.all([api.get("/provider/stats"), api.get("/provider/bookings")]);
        setStats(s.data);
        setBookings(b.data);
      }
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (failed && !data) return <div className="mx-auto max-w-xl px-4 py-20"><EmptyState title="Could not load your dashboard" text="Please refresh the page or sign in again." testId="dashboard-error" /></div>;
  if (!data) return <PageLoader />;
  const p = data.provider;
  if (!p) return (
    <div className="mx-auto max-w-xl px-4 py-20">
      <EmptyState icon={Store} title="No business profile yet" text="Create your business profile to start receiving bookings." testId="no-provider-profile"
        action={<Link to="/list-your-business" className="rounded-xl bg-sky-600 px-5 py-3 text-sm font-semibold text-white" data-testid="create-profile-link">List Your Business</Link>} />
    </div>
  );
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Pacific/Auckland" });
  const todays = bookings.filter((b) => b.date === today && b.status !== "cancelled");

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="provider-dashboard">
      <DashboardHeader p={p} />
      <StatsGrid stats={stats} />
      <Tabs defaultValue="overview" className="mt-8">
        <TabsList className="flex h-auto w-full justify-start gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1 no-scrollbar">
          {[["overview", "Overview"], ["bookings", "Bookings"], ["services", "Services"], ["availability", "Availability"], ["reviews", "Reviews"], ["profile", "Business profile"]].map(([k, l]) => (
            <TabsTrigger key={k} value={k} data-testid={`pd-tab-${k}`} className="h-10 rounded-lg px-4 text-sm font-semibold">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview" className="mt-6"><OverviewTab todays={todays} bookings={bookings} reload={load} today={today} /></TabsContent>
        <TabsContent value="bookings" className="mt-6"><ProviderBookings bookings={bookings} reload={load} today={today} /></TabsContent>
        <TabsContent value="services" className="mt-6"><ProviderServices /></TabsContent>
        <TabsContent value="availability" className="mt-6"><ProviderAvailability provider={p} onSaved={load} /></TabsContent>
        <TabsContent value="reviews" className="mt-6"><ProviderReviews /></TabsContent>
        <TabsContent value="profile" className="mt-6"><ProviderProfileForm provider={p} onSaved={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
